import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  changeDeviceName,
  changeDeviceState,
  deleteDevice,
  deleteDeviceData,
  getDevicesList,
} from "../../services/devices"
import { initialFilterState, themePalette } from "./styles"
import {
  buildDevicesFromResponse,
  buildProductFilterOptions,
  countActiveMeters,
  filterDevicesTree,
  findMeterById,
  findParentMeterByChildId,
  isBlockedByParent,
  removeMeterFromDevices,
  updateMeterInDevices,
} from "./devicesSettings.utils"

const SIMULATED_REQUEST_TIME = 900
const RIGHT_PANEL_LOADING_TIME = 400

// Hook contenedor del estado y las acciones de la pantalla.
// Separa negocio y coordinacion del JSX principal para que el componente quede mas facil de leer.
export const useDevicesSettingsData = () => {
  const hasInitializedRightPanel = useRef(false)
  const rightPanelLoadingTimeoutRef = useRef(null)

  const [devices, setDevices] = useState([])
  const [selectedMeter, setSelectedMeter] = useState(null)
  const [editableName, setEditableName] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [affirmModalState, setAffirmModalState] = useState({ open: false, type: null, meterId: null })
  const [selectedFilters, setSelectedFilters] = useState(initialFilterState)
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null)
  const [mobileView, setMobileView] = useState("list")
  const [close, setClose] = useState(false)
  const [msg, setMsg] = useState({
    msg: "",
    type: "success",
  })
  const [initialLoading, setInitialLoading] = useState(true)
  const [loadingSave, setLoadingSave] = useState(false)
  const [loadingBlurState, setLoadingBlurState] = useState(false)
  const [loadingStateChange, setLoadingStateChange] = useState(false)
  const [rightPanelLoading, setRightPanelLoading] = useState(false)
  const [expandedMeters, setExpandedMeters] = useState(new Set())

  useEffect(() => {
    setEditableName(selectedMeter?.name || "")
  }, [selectedMeter?.id, selectedMeter?.name])

  useEffect(() => {
    let isMounted = true
    let deferredProcessingId = null
    const scheduleAfterPaint = (callback) => {
      if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
        deferredProcessingId = window.requestAnimationFrame(callback)
        return
      }
      deferredProcessingId = setTimeout(callback, 0)
    }
    const cancelScheduledProcessing = () => {
      if (deferredProcessingId === null) return

      if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(deferredProcessingId)
      } else {
        clearTimeout(deferredProcessingId)
      }
      deferredProcessingId = null
    }
    const fetchDevices = async () => {
      setInitialLoading(true)
      try {
        const response = await getDevicesList()
        if (!isMounted) return
        setInitialLoading(false)
        scheduleAfterPaint(() => {
          if (!isMounted) return
          const { devices: nextDevices, expandedMeterIds } = buildDevicesFromResponse(response?.data || {})
          setDevices(nextDevices)
          setSelectedMeter(null)
          setEditableName("")
          setExpandedMeters(expandedMeterIds)
          deferredProcessingId = null
        })
      } catch (error) {
        if (!isMounted) return

        setInitialLoading(false)
        setDevices([])
        setSelectedMeter(null)
        setEditableName("")
        setExpandedMeters(new Set())
        setMsg({
          msg: error?.response?.data?.msg || "No pudimos cargar los dispositivos reales.",
          type: "error",
        })
        setClose(true)
      }
    }
    fetchDevices()
    return () => {
      isMounted = false
      cancelScheduledProcessing()
    }
  }, [])

  useEffect(() => {
    if (!selectedMeter?.id) return undefined
    if (!hasInitializedRightPanel.current) {
      hasInitializedRightPanel.current = true
      return undefined
    }
    setRightPanelLoading(true)
    if (rightPanelLoadingTimeoutRef.current) {
      clearTimeout(rightPanelLoadingTimeoutRef.current)
    }
    rightPanelLoadingTimeoutRef.current = setTimeout(() => {
      setRightPanelLoading(false)
    }, RIGHT_PANEL_LOADING_TIME)
    return () => {
      if (rightPanelLoadingTimeoutRef.current) {
        clearTimeout(rightPanelLoadingTimeoutRef.current)
      }
    }
  }, [selectedMeter?.id])

  useEffect(() => () => {
    if (rightPanelLoadingTimeoutRef.current) {
      clearTimeout(rightPanelLoadingTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (!close) return undefined
    const timeout = setTimeout(() => {
      setClose(false)
    }, 7000)
    return () => clearTimeout(timeout)
  }, [close])

  const productFilterOptions = useMemo(() => buildProductFilterOptions(devices), [devices])
  const normalizedSearchTerm = searchTerm.trim().toLowerCase()
  const activeFilterCount = useMemo(
    () => Object.values(selectedFilters).reduce((total, values) => total + values.length, 0),
    [selectedFilters]
  )
  const isFilteringDevices = Boolean(normalizedSearchTerm) || activeFilterCount > 0
  const activeMetersCount = useMemo(() => countActiveMeters(devices), [devices])

  const filterSections = useMemo(() => [
    {
      key: "activity",
      title: "Estado",
      options: [
        { value: "activo", label: "Activos" },
        { value: "inactivo", label: "Inactivos" },
      ],
    },
    {
      key: "products",
      title: "Producto",
      options: productFilterOptions,
    },
    {
      key: "cloudAccess",
      title: "Acceso a la nube",
      options: [
        { value: "permanente", label: "Acceso permanente" },
        { value: "trial", label: "Acceso de prueba" },
        { value: "sin-acceso", label: "Sin acceso" },
      ],
    },
    {
      key: "nodeAssociation",
      title: "Nodo",
      options: [
        { value: "asociado", label: "Asociado a nodo" },
        { value: "sin-asociar", label: "Sin nodo asociado" },
      ],
    },
  ], [productFilterOptions])

  const matchesMeterSearch = useCallback((meter) => {
    if (!normalizedSearchTerm) return true

    return [meter.name, meter.key]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(normalizedSearchTerm))
  }, [normalizedSearchTerm])

  const matchesMeterFilters = useCallback((meter) => {
    const isParentMeter = Boolean(meter.children?.length)
    const hasNodeAssociationData = typeof meter.associatedToNode === "boolean"
    const matchesActivity =
      selectedFilters.activity.length === 0 ||
      selectedFilters.activity.includes(meter.active ? "activo" : "inactivo")
    const matchesProduct =
      selectedFilters.products.length === 0 || selectedFilters.products.includes(meter.model)
    const matchesCloudAccess =
      selectedFilters.cloudAccess.length === 0 || selectedFilters.cloudAccess.includes(meter.cloudAccess)
    const matchesNodeAssociation =
      selectedFilters.nodeAssociation.length === 0 ||
      (isParentMeter ? false :
        !hasNodeAssociationData ||
        selectedFilters.nodeAssociation.includes(meter.associatedToNode ? "asociado" : "sin-asociar"))

    return matchesActivity && matchesProduct && matchesCloudAccess && matchesNodeAssociation
  }, [selectedFilters])

  const filteredDevices = useMemo(
    () => filterDevicesTree(devices, isFilteringDevices, matchesMeterSearch, matchesMeterFilters),
    [devices, isFilteringDevices, matchesMeterFilters, matchesMeterSearch]
  )

  const selectedParentMeter = selectedMeter ? findParentMeterByChildId(devices, selectedMeter.id) : null
  const selectedIsChild = Boolean(selectedParentMeter)
  const selectedCloudSource = selectedIsChild ? selectedParentMeter : selectedMeter
  const selectedHasChildren = Boolean(selectedMeter?.children?.length)
  const selectedBlockedByParent = selectedMeter ? isBlockedByParent(devices, selectedMeter.id) : false
  const selectedCloudButtonDisabled = selectedBlockedByParent || selectedIsChild
  const shouldShowCloudActionButton = !selectedIsChild && selectedCloudSource?.cloudAccess === "trial"

  const updateMeter = useCallback((meterId, updates) => {
    setDevices((prevDevices) => {
      const nextDevices = updateMeterInDevices(prevDevices, meterId, updates)
      setSelectedMeter((prevSelectedMeter) => (
        prevSelectedMeter ? findMeterById(nextDevices, prevSelectedMeter.id) || prevSelectedMeter : prevSelectedMeter
      ))
      return nextDevices
    })
  }, [])

  const removeMeter = useCallback((meterId) => {
    setDevices((prevDevices) => {
      const parentBeforeRemoval = findParentMeterByChildId(prevDevices, meterId)
      const nextDevices = removeMeterFromDevices(prevDevices, meterId)
      const fallbackSelectedMeter =
        (parentBeforeRemoval && findMeterById(nextDevices, parentBeforeRemoval.id)) ||
        nextDevices[0]?.groups[0]?.meters[0] ||
        null
      setSelectedMeter((prevSelectedMeter) => {
        if (!prevSelectedMeter || prevSelectedMeter.id === meterId) return fallbackSelectedMeter
        return findMeterById(nextDevices, prevSelectedMeter.id) || fallbackSelectedMeter
      })

      return nextDevices
    })
  }, [])

  const simulateEndpointRequest = useCallback(
    () =>
      new Promise((resolve) => {
        setTimeout(resolve, SIMULATED_REQUEST_TIME)
      }),
    []
  )

  const openAffirmModal = useCallback((type, meterId = selectedMeter?.id) => {
    setAffirmModalState({ open: true, type, meterId })
  }, [selectedMeter?.id])

  const closeAffirmModal = useCallback(() => {
    setAffirmModalState({ open: false, type: null, meterId: null })
  }, [])

  const runSimulatedAction = useCallback(async (action, successMessage) => {
    setLoadingBlurState(true)
    try {
      await simulateEndpointRequest()
      action()
      setMsg({
        msg: successMessage,
        type: "success",
      })
      setClose(true)
    } finally {
      setLoadingBlurState(false)
    }
  }, [simulateEndpointRequest])

  const runDeviceAction = useCallback(async ({
    meterId,
    request,
    onSuccess,
    successMessage,
    fallbackErrorMessage,
  }) => {
    const currentMeter = findMeterById(devices, meterId)
    const deviceKey = currentMeter?.requestKey || currentMeter?.key

    if (!deviceKey) {
      setMsg({
        msg: "No pudimos identificar el dispositivo seleccionado.",
        type: "error",
      })
      setClose(true)
      return
    }

    setLoadingBlurState(true)
    try {
      const response = await request(deviceKey)
      onSuccess?.()
      setMsg({
        msg: response?.data?.msg || successMessage,
        type: "success",
      })
      setClose(true)
    } catch (error) {
      setMsg({
        msg: error?.response?.data?.msg || fallbackErrorMessage,
        type: "error",
      })
      setClose(true)
    } finally {
      setLoadingBlurState(false)
    }
  }, [devices])

  const persistMeterStateChange = useCallback(async (meterId) => {
    const currentMeter = findMeterById(devices, meterId)
    if (!currentMeter || currentMeter.children?.length) return
    const nextActiveState = !currentMeter.active
    try {
      setLoadingStateChange(true)
      await changeDeviceState(currentMeter.requestKey || currentMeter.key, {
        active: nextActiveState,
      })
      updateMeter(meterId, { active: nextActiveState })
      setMsg({
        msg: nextActiveState ? "Su activacion fue exitosa" : "Su dispositivo ya se encuentra inactivo",
        type: "success",
      })
      setClose(true)
    } catch (error) {
      setMsg({
        msg: error?.response?.data?.msg || "Ocurrio un error inesperado al cambiar el estado del dispositivo",
        type: "error",
      })
      setClose(true)
    } finally {
      setLoadingStateChange(false)
    }
  }, [devices, updateMeter])

  const handleMeterStatusChange = useCallback((meterId) => {
    const currentMeter = findMeterById(devices, meterId)
    if (!currentMeter || currentMeter.children?.length || loadingStateChange) return
    if (currentMeter.active) {
      openAffirmModal("deactivate-meter", meterId)
      return
    }
    persistMeterStateChange(meterId)
  }, [devices, loadingStateChange, openAffirmModal, persistMeterStateChange])

  const updateCloudAccess = useCallback((meterId, nextStatus) => {
    updateMeter(meterId, {
      cloudAccess: nextStatus,
      remainingDays: nextStatus === "permanente" ? selectedMeter?.remainingDays : undefined,
    })
  }, [selectedMeter?.remainingDays, updateMeter])

  const handleCloudAccessAction = useCallback(async (meterId, nextStatus) => {
    await runSimulatedAction(() => {
      updateCloudAccess(meterId, nextStatus)
    }, nextStatus === "permanente" ? "El acceso a la nube fue activado" : "El acceso a la nube fue desactivado")
  }, [runSimulatedAction, updateCloudAccess])

  const handleNameBlur = useCallback(async () => {
    const nextName = editableName.trim()
    if (!selectedMeter || !nextName) return

    if (nextName === selectedMeter.name) {
      setMsg({
        msg: "El nombre que desea guardar es el mismo que el actual",
        type: "warning",
      })
      setClose(true)
      return
    }

    try {
      setLoadingSave(true)
      await changeDeviceName(selectedMeter.requestKey || selectedMeter.key, {
        device_name: nextName,
      })
      updateMeter(selectedMeter.id, { name: nextName })
      setClose(true)
      setMsg({
        msg: "Cambio de nombre realizado correctamente",
        type: "success",
      })
    } catch (error) {
      setClose(true)
      setMsg({
        msg: error?.response?.data?.msg || "Ocurrio un error inesperado",
        type: "error",
      })
    } finally {
      setLoadingSave(false)
    }
  }, [editableName, selectedMeter, updateMeter])

  const handleOpenFilters = useCallback((event) => {
    setFiltersAnchorEl(event.currentTarget)
  }, [])

  const handleCloseFilters = useCallback(() => {
    setFiltersAnchorEl(null)
  }, [])

  const toggleFilterValue = useCallback((sectionKey, optionValue) => {
    setSelectedFilters((prev) => {
      const currentValues = prev[sectionKey]
      const nextValues = currentValues.includes(optionValue)
        ? currentValues.filter((value) => value !== optionValue)
        : [...currentValues, optionValue]

      return {
        ...prev,
        [sectionKey]: nextValues,
      }
    })
  }, [])

  const clearAllFilters = useCallback(() => {
    setSelectedFilters(initialFilterState)
  }, [])

  const handleAffirmAction = useCallback(async () => {
    const { type, meterId } = affirmModalState
    switch (type) {
      case "deactivate-meter":
        closeAffirmModal()
        await persistMeterStateChange(meterId)
        return
      case "delete-device":
        closeAffirmModal()
        await runDeviceAction({
          meterId,
          request: deleteDevice,
          onSuccess: () => {
            removeMeter(meterId)
          },
          successMessage: "Su dispositivo fue eliminado",
          fallbackErrorMessage: "Ocurrio un error inesperado al eliminar el dispositivo",
        })
        return
      case "delete-data":
        closeAffirmModal()
        await runDeviceAction({
          meterId,
          request: deleteDeviceData,
          successMessage: "Sus datos fueron eliminados",
          fallbackErrorMessage: "Ocurrio un error inesperado al eliminar los datos del dispositivo",
        })
        return
      default:
        break
    }
    closeAffirmModal()
  }, [affirmModalState, closeAffirmModal, persistMeterStateChange, removeMeter, runDeviceAction])

  const affirmModalCopy = useMemo(() => {
    switch (affirmModalState.type) {
      case "deactivate-meter":
        return {
          title: "Desactivar medidor",
          description: "¿Estás seguro de que deseas desactivar este medidor? Si lo desactivás, dejaremos de recibir información y todos los datos almacenados se eliminarán de forma permanente.",
          confirmLabel: "Si, desactivar",
          confirmColor: themePalette.primary.main,
        }
      case "delete-data":
        return {
          title: "Eliminar datos",
          description: "¿Estás seguro de que deseas eliminar los datos de este dispositivo? Si continuás, toda la información histórica almacenada se eliminará de forma permanente.",
          confirmLabel: "Si, eliminar datos",
          confirmColor: themePalette.error.main,
        }
      case "delete-device":
        return {
          title: "Eliminar dispositivo",
          description: "¿Estás seguro de que deseas eliminar este dispositivo? Si lo eliminás, su configuración y el acceso desde esta pantalla se quitarán de forma permanente.",
          confirmLabel: "Si, eliminar dispositivo",
          confirmColor: themePalette.error.main,
        }
      default:
        return {
          title: "",
          description: "",
          confirmLabel: "Aceptar",
          confirmColor: themePalette.primary.main,
        }
    }
  }, [affirmModalState.type])

  return {
    devices,
    selectedMeter,
    setSelectedMeter,
    editableName,
    setEditableName,
    searchTerm,
    setSearchTerm,
    affirmModalState,
    selectedFilters,
    filtersAnchorEl,
    mobileView,
    setMobileView,
    close,
    setClose,
    msg,
    initialLoading,
    loadingSave,
    loadingBlurState,
    loadingStateChange,
    rightPanelLoading,
    expandedMeters,
    setExpandedMeters,
    activeFilterCount,
    isFilteringDevices,
    activeMetersCount,
    filterSections,
    filteredDevices,
    selectedParentMeter,
    selectedIsChild,
    selectedCloudSource,
    selectedHasChildren,
    selectedBlockedByParent,
    selectedCloudButtonDisabled,
    shouldShowCloudActionButton,
    affirmModalCopy,
    handleOpenFilters,
    handleCloseFilters,
    toggleFilterValue,
    clearAllFilters,
    handleNameBlur,
    handleMeterStatusChange,
    handleCloudAccessAction,
    openAffirmModal,
    closeAffirmModal,
    handleAffirmAction,
  }
}
